import { ref } from "vue";
import { defineStore } from "pinia";
import { useSignalR } from "@/composables/signalr";
import {
  type ChoreTileDto,
  type CreateChoreInstanceDto,
  type CreatePerformedChoreDto,
  type CreateAndPerformOneTimeChoreDto,
  type CreatePerformedChoresResponseDto,
  getApiV1ChoreBoard,
  putApiV1ChoreInstancesById,
  postApiV1PerformedChores,
  postApiV1ChoreInstances,
} from "@/client";
import type {
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  XpUpdateEvent,
} from "@/types/signalr-events";
export const useTaskBoardStore = defineStore("taskBoard", () => {
  const isLoading = ref(false);
  const isLoaded = ref(false);
  const choreTiles = ref<ChoreTileDto[]>([]);
  const { subscribe, onReconnected } = useSignalR();
  // Live events sent while offline are lost; reload what this store already showed.
  onReconnected(() => {
    if (isLoaded.value) {
      fetchChoreTiles().catch((err) => console.error("Resync after reconnect failed", err));
    }
  });
  const performedChoreSummary = ref<CreatePerformedChoresResponseDto | null>(
    null,
  );

  const lastXpUpdate = ref<XpUpdateEvent | null>(null);

  async function fetchChoreTiles() {
    isLoading.value = true;
    try {
      const response = await getApiV1ChoreBoard();
      if (response.error) {
        console.error("Error fetching chore tiles:", response.error);
        throw new Error("Failed to fetch chore tiles");
      }
      choreTiles.value = response.data!;
      console.log(response.data);
      isLoaded.value = true;
    } finally {
      isLoading.value = false;
    }
  }

  async function ensureChoreTilesLoaded() {
    if (!isLoaded.value) {
      await fetchChoreTiles();
    }
  }

  async function updateChoreInstance(
    choreTile: ChoreTileDto,
    userId: string | null | undefined,
    dueAt: Date | null | undefined,
  ) {
    try {
      const choreInstanceId = choreTile.choreInstanceId;
      if (!choreInstanceId) {
        console.warn("Chore instance not found for chore tile:", choreTile);
        return;
      }
      const response = await putApiV1ChoreInstancesById({
        path: { id: choreInstanceId },
        body: {
          id: choreInstanceId,
          dueAt: dueAt,
          assignedUserId: userId,
          status: choreTile.status,
        },
      });
      if (response.error) {
        console.error("Error updating chore instance:", response.error);
        throw new Error("Failed to update chore instance");
      }
    } catch (error) {
      console.error("Error updating chore instance assignee:", error);
      throw error; // Re-throw to handle it in the component
    }
  }

  async function performChores(
    performedChores: CreatePerformedChoreDto[],
    performedOneTimeChores: CreateAndPerformOneTimeChoreDto[],
    participantUserIds: string[],
    performedAt: Date,
  ) {
    try {
      const response = await postApiV1PerformedChores({
        body: {
          performedChores,
          performedOneTimeChores,
          participantUserIds,
          performedAt,
        },
      });
      if (response.error) {
        console.error("Error performing chore:", response.error);
        throw new Error("Failed to perform chore");
      }
      return response.data!;
    } catch (error) {
      console.error("Error performing chore:", error);
      throw error; // Re-throw to handle it in the component
    }
  }

  async function scheduleChore(choreInstance: CreateChoreInstanceDto) {
    try {
      const response = await postApiV1ChoreInstances({
        body: choreInstance,
      });
      if (response.error) {
        console.error("Error scheduling chore:", response.error);
        throw new Error("Failed to schedule chore");
      }
      return response.data!;
    } catch (error) {
      console.error("Error scheduling chore:", error);
      throw error; // Re-throw to handle it in the component
    }
  }

  subscribe<EntityUpdatedEvent<ChoreTileDto>>(
    "ChoreTileUpdated",
    async (event) => {
      console.debug("ChoreTileUpdated event received:", event);
      createOrUpdateChoreTile(event.entity);
    },
  );

  subscribe<EntityCreatedEvent<ChoreTileDto>>(
    "ChoreTileCreated",
    async (event) => {
      console.debug("ChoreTileCreated event received:", event);
      createOrUpdateChoreTile(event.entity);
    },
  );

  subscribe<EntityDeletedEvent>("ChoreTileDeleted", async (event) => {
    console.debug("ChoreTileDeleted event received:", event);
    const index = choreTiles.value.findIndex(
      (tile) => tile.choreId === event.entityId,
    );
    if (index !== -1) {
      choreTiles.value.splice(index, 1);
    }
  });

  subscribe<XpUpdateEvent>("XpUpdated", async (event) => {
    console.debug("XpUpdated event received:", event);
    lastXpUpdate.value = event;
  });

  function createOrUpdateChoreTile(entity: ChoreTileDto) {
    const index = choreTiles.value.findIndex(
      (tile) => tile.choreId === entity.choreId,
    );
    if (!(entity.dueAt instanceof Date)) {
      entity.dueAt = entity.dueAt ? new Date(entity.dueAt) : null;
    }
    if (!(entity.lastPerformedAt instanceof Date)) {
      entity.lastPerformedAt = entity.lastPerformedAt
        ? new Date(entity.lastPerformedAt)
        : null;
    }
    if (index === -1) {
      choreTiles.value.push(entity);
    } else {
      choreTiles.value[index] = entity;
    }
  }

  return {
    choreTiles,
    choreTilesLoading: isLoading,
    choreTilesLoaded: isLoaded,
    performedChoreSummary,
    lastXpUpdate,
    fetchChoreTiles,
    ensureChoreTilesLoaded,
    performChores,
    scheduleChore,
    updateChoreInstance,
  };
});
